import { createContext, useContext, useEffect, useRef } from 'react';

import { createStore, StoreApi } from 'zustand';
import { useStoreWithEqualityFn } from 'zustand/traditional';

import { TrackingUrlArray, VariablePluginOption } from '@anyx/feature-crm/shared';


import { LineMessageBlockType, LineMessageBlockTypeWithImageUpload } from '../types';

export interface LineMessageFormStoreProps {
  /** flag of disable all the form inputs */
  isFormDisabled?: boolean;
  /** method to upload image */
  onImageUpload?: (
    file: File,
    type: LineMessageBlockTypeWithImageUpload
  ) => Promise<string | undefined>;
  /** method to generate tracking link */
  onGenerateTrackingLink?: (
    urls: string[],
    opts: { urlLinkGroupId: string; shorten?: boolean }
  ) => Promise<TrackingUrlArray>;
  /** Variable Plugin Options */
  variablePluginOptions?: VariablePluginOption[];
}

export type LineMessageFormDefaultView = {
  name: 'DEFAULT';
  blockIndex?: never;
  cardIndex?: never;
};

export type LineMessageFormSubView = {
  name: LineMessageBlockType;
  blockIndex: number;
  cardIndex?: number;
};

export interface LineMessageFormStoreState extends LineMessageFormStoreProps {
  /** states */
  view: LineMessageFormDefaultView | LineMessageFormSubView;
  formHeight: number | 'auto';
  /** actions */
  navigateToDefaultView: () => void;
  navigateToBlockView: (view: LineMessageBlockType, blockIndex: number, cardIndex?: number) => void;
  setFormHeight: (height: number | 'auto') => void;
}

type LineMessageStore = ReturnType<typeof createLineMessageStore>;

const createLineMessageStore = (props: LineMessageFormStoreProps) => {
  return createStore<LineMessageFormStoreState>()((set) => ({
    ...props,
    view: {
      name: 'DEFAULT',
    },
    formHeight: 'auto',
    navigateToDefaultView: () => {
      set({
        view: {
          name: 'DEFAULT',
        },
      });
    },
    navigateToBlockView: (name, blockIndex, cardIndex) => {
      set({
        view: {
          name,
          blockIndex,
          cardIndex,
        },
      });
    },
    setFormHeight: (height: number | 'auto') => {
      set({
        formHeight: height,
      });
    },
  }));
};

export const LineMessageContext = createContext<LineMessageStore | null>(null);

const LineMessageFormStoreUpdater = (dynamicProps: LineMessageFormStoreProps) => {
  const { setState } = useContext(LineMessageContext) || {};

  if (!setState) throw new Error('Missing LineMessageContext.Provider in the tree');

  useEffect(() => {
    setState({
      ...dynamicProps,
    });
  }, [dynamicProps, setState]);

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <></>;
};

export function LineMessageProvider({
  children,
  ...props
}: React.PropsWithChildren<LineMessageFormStoreProps>) {
  const storeRef = useRef<LineMessageStore>(null);

  if (!storeRef.current) {
    storeRef.current = createLineMessageStore(props);
  }

  return (
    <LineMessageContext.Provider value={storeRef.current}>
      <LineMessageFormStoreUpdater {...props} />
      {children}
    </LineMessageContext.Provider>
  );
}

export function useLineMessageStore<T>(
  selector: (state: LineMessageFormStoreState) => T,
  equalityFn?: (left: T, right: T) => boolean
): T {
  const store = useContext(LineMessageContext);
  if (!store) throw new Error('Missing LineMessageContext.Provider in the tree');
  return useStoreWithEqualityFn(store, selector, equalityFn);
}

/** This is for external form create useStore hook to access the store with their own additional props */
export function createExternalLineMessageStore<
  ExternalStoreState extends LineMessageFormStoreProps,
>(): <T>(
  selector: (state: ExternalStoreState & LineMessageFormStoreState) => T,
  equalityFn?: (left: T, right: T) => boolean
) => T {
  return (selector, equalityFn) => {
    const store = useContext(LineMessageContext) as StoreApi<
      ExternalStoreState & LineMessageFormStoreState
    > | null;
    if (!store) throw new Error('Missing LineMessageContext.Provider in the tree');

    return useStoreWithEqualityFn(store, selector, equalityFn);
  };
}
